import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';

const SealedWhisperCard = ({ entry, getUserDisplayName }) => {
    const { t } = useTranslation();
    const { user, userProfiles, appFunctions, setMessage } = useAppContext();
    const [bidAmount, setBidAmount] = useState(10);
    const [isBidding, setIsBidding] = useState(false);
    const currentUserProfile = userProfiles.find(p => p.id === user?.uid);

    const handleBid = async () => {
        if (!user) { setMessage(t('sealedWhisper.signInRequired')); return; }
        if (bidAmount <= 0) { setMessage(t('sealedWhisper.positiveBid')); return; }
        if (currentUserProfile.tokens < bidAmount) { setMessage(t('sealedWhisper.notEnoughEchoes')); return; }
        setIsBidding(true);
        const bidOnSealedWhisper = httpsCallable(appFunctions, 'bidOnSealedWhisper');
        try {
            await bidOnSealedWhisper({ whisperId: entry.id, amount: bidAmount });
            setMessage(t('sealedWhisper.bidSuccess', { amount: bidAmount }));
        } catch (error) {
            console.error("Error placing bid:", error);
            setMessage(t('sealedWhisper.bidError', { message: error.message }));
        } finally {
            setIsBidding(false);
        }
    };

    const unsealDate = entry.unsealTimestamp.toDate();
    const now = new Date();
    const timeLeft = unsealDate > now ? unsealDate - now : 0;
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    return (
        <div className="bg-gradient-to-br from-gray-900 to-purple-900/50 p-6 rounded-lg shadow-2xl mb-6 border-2 border-purple-400/30 transition-all duration-300 hover:shadow-purple-500/40 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-black/20"></div>
            <div className="relative z-10">
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h3 className="text-2xl font-bold text-white font-playfair flex items-center gap-2"><LucideIcons.Lock size={20} /> {entry.sealTitle}</h3>
                        <p className="text-xs text-gray-400">{t('sealedWhisper.sealedBy', { user: getUserDisplayName(entry.authorId) })} ({t('sealedWhisper.reputation')}: {entry.authorReputationAtSeal})</p>
                    </div>
                    <div className="text-center bg-black/30 px-4 py-2 rounded-lg border border-purple-500/50 animate-pulse-slow">
                        <p className="text-xs text-purple-300">{t('sealedWhisper.unsealsIn')}</p>
                        <p className="font-bold text-lg text-white">{days}d {hours}h</p>
                    </div>
                </div>
                <div className="text-center my-8">
                    <p className="text-sm text-gray-400">{t('sealedWhisper.totalBidPool')}</p>
                    <p className="text-6xl font-bold text-yellow-300 flex items-center justify-center drop-shadow-lg">
                        <LucideIcons.Flame size={48} className="mr-3" />
                        {entry.sealBidPool || 0}
                    </p>
                </div>
                <div className="flex items-center space-x-3">
                    <input type="number" value={bidAmount} onChange={(e) => setBidAmount(Number(e.target.value))} className="shadow appearance-none border-2 border-purple-400/50 rounded-full w-full py-3 px-5 bg-gray-800/70 text-white leading-tight focus:outline-none focus:ring-2 focus:ring-yellow-400" min="1" />
                    <button onClick={handleBid} disabled={isBidding} className="px-8 py-3 bg-yellow-500 text-black font-bold rounded-full hover:bg-yellow-400 transition duration-300 disabled:opacity-50 flex items-center justify-center">
                        {isBidding ? <div className="action-spinner" /> : t('sealedWhisper.placeBid')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SealedWhisperCard;